<template>
    <header class="flex items-center gap-3 h-16 border-b border-default bg-default px-4 sm:px-6">
        <UButton
            icon="i-lucide-menu"
            color="neutral"
            variant="ghost"
            class="lg:hidden"
            :aria-label="$t('general.actions')"
            @click="sidebarOpen = true"
        />

        <h1 class="text-lg font-semibold text-highlighted truncate">{{ pageTitle }}</h1>

        <div class="ml-auto flex items-center gap-2">
            <UButton
                v-if="canBook"
                icon="i-lucide-plus"
                color="primary"
                :label="$t('dashboard.new_reservation')"
                class="hidden sm:inline-flex"
                @click="wizardStore.openWizard()"
            />
            <UButton
                v-if="canBook"
                icon="i-lucide-plus"
                color="primary"
                class="sm:hidden"
                :aria-label="$t('dashboard.new_reservation')"
                @click="wizardStore.openWizard()"
            />

            <UColorModeButton />

            <UChip
                :text="notificationStore.unread"
                :show="notificationStore.unread > 0"
                color="error"
                size="3xl"
                :ui="{ base: 'text-[11px] font-semibold px-1 min-w-[18px] h-[18px] rounded-full ring-2 ring-default' }"
            >
                <UButton
                    icon="i-lucide-bell"
                    color="neutral"
                    variant="ghost"
                    :aria-label="$t('nav.notifications')"
                    @click="router.push('/obvestila')"
                />
            </UChip>

            <UDropdownMenu :items="menuItems">
                <UButton color="neutral" variant="ghost" class="gap-2">
                    <UAvatar :text="initials" size="xs" />
                    <span class="hidden sm:inline text-sm font-medium">{{ fullName }}</span>
                    <UIcon name="i-lucide-chevron-down" class="size-4" />
                </UButton>
            </UDropdownMenu>
        </div>
    </header>
</template>

<script lang="ts" setup>
    import { computed, watch } from 'vue'
    import { useRoute, useRouter } from 'vue-router'
    import { useI18n } from 'vue-i18n'
    import type { DropdownMenuItem } from '@nuxt/ui'

    import { sidebarOpen } from '@/layouts/layout'
    import { useAppStore } from '@/stores/app'
    import { useWizardStore } from '@/stores/wizard'
    import { useNotificationStore } from '@/stores/notification'
    import { userInitials } from '@/libs/domain'
    import { UserRole } from '@/enums/domain'

    const route  = useRoute()
    const router = useRouter()
    const { t }  = useI18n()

    const appStore = useAppStore()
    const wizardStore = useWizardStore()
    const notificationStore = useNotificationStore()

    const titleKeys: Record<string, string> = {
        dashboard:       'nav.dashboard',
        reservations:    'nav.reservations',
        devices:         'nav.devices',
        probes:          'nav.probes',
        users:           'nav.users',
        groups:          'nav.groups',
        clients:         'nav.clients',
        quotas:          'nav.quotas',
        confirmations:   'nav.confirmations',
        'probe-plan':    'nav.probe_plan',
        downtimes:       'nav.downtimes',
        notifications:   'nav.notifications',
        settings:        'nav.settings',
        profile:         'nav.profile',
        'access-denied': 'general.access_denied',
        'not-found':     'general.not_found',
    }

    // The read-only reporter role never books (v1.16).
    const canBook = computed(() => appStore.userData?.role !== UserRole.Reporter)

    const pageTitle = computed(() => {
        const key = titleKeys[route.name as string]
        return key ? t(key) : t('app.name')
    })

    const fullName = computed(() =>
        appStore.userData ? `${appStore.userData.first_name} ${appStore.userData.last_name}` : '',
    )
    const initials = computed(() => userInitials(fullName.value))

    const logout = () => {
        appStore.setLogout()
        router.push({ name: 'login' })
    }

    const menuItems = computed<DropdownMenuItem[][]>(() => [
        [
            { label: t('profile.my_profile'), icon: 'i-lucide-user',     to: '/profil' },
            { label: t('nav.settings'),       icon: 'i-lucide-settings', to: '/nastavitve' },
        ],
        [
            { label: t('profile.logout'), icon: 'i-lucide-log-out', color: 'error', onSelect: logout },
        ],
    ])

    // Poll unread + pending-confirmation counts on route change.
    watch(() => route.path, () => {
        notificationStore.refreshUnread()
        notificationStore.refreshPending()
    }, { immediate: true })
</script>
